<template>
	<div class="super-business-section">
		<AppCard>
			<template #header>
				<div class="container-search">
					<div class="title">{{ getString('businesses') }}</div>
					<AppTextInput icon-name="icon-magnify" :placeholder="getString('search')" class="search-input" @changed-text="changeSearch"/>
				</div>
			</template>
			<template #body>
				<p v-if="businesses.length === 0" class="no-results">{{ getString('no_results') }}</p>
				<AppList v-if="businesses.length !== 0" :items="businesses">
					<template #business="{item}">
						<div @click="toggleBusiness(item)" class="list-item-header">
							<p class="list-item-title">
								<span class="deleted" v-if="item.deleted">{{ getString('deleted') }}</span>
								{{ item.name }}
								<span class="verified-badge" v-if="!!item.verified_user_id" v-html="svgStore.get('iconTick')"
									  :title="getString('verified_identity')"></span>
							</p>
							<div class="list-item-business-num">{{ '#' + item.business_id }}</div>
						</div>
					</template>
					<template #businessHeader="{item}">
						<div class="business-header-row">
							<p @click="toggleBusiness(item)" class="business-link"
							   v-html="getBusinessLinkHtml(item)"></p>
							<p v-if="item.active_plan" class="active-plan">{{ item.active_plan }}</p>
						</div>
					</template>
					<template #businessDropdown="{item}">
						<BusinessSuperDropdown :business="item"/>
					</template>
				</AppList>
				<PaginationRow v-if="businesses.length !== 0" :page="page" :page-size="pageSize" :total="businessesSearched.length"
							   :initial-page-size="10" @page-changed="pageChanged"/>
			</template>
		</AppCard>
		<div class="row centered mt-30" v-if="superStore.isUltra || superStore.isMega">
			<SuperLogs/>
		</div>
	</div>
</template>

<script>
import AppCard from "@/shared/components/AppCard.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import {useSuperStore, useUserStore} from "@/user/stores";
import {useLanguageStore, useSvgStore} from "@/shared/stores";
import {formatDateTime} from "@/shared/helpers/dates";
import AppList from "@/shared/components/AppList.vue";
import PaginationRow from "@/console-new/components/shared/PaginationRow.vue";
import BusinessSuperDropdown from "@/user/components/super/BusinessSuperDropdown.vue";
import SuperLogs from "@/user/components/super/SuperLogs.vue";

export default {
	setup() {
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		const superStore = useSuperStore();
		const svgStore = useSvgStore();
		return {userStore, formatDateTime, getString: languageStore.getString, svgStore, superStore}
	},
	name: "SuperBusinessAdministration",
	components: {BusinessSuperDropdown, AppList, AppTextInput, AppCard, PaginationRow, SuperLogs},
	data() {
		return {
			page: 0,
			pageSize: 10,
			HOST: HOST,
			search: "",
		};
	},
	computed: {
		businesses() {
			return this.businessesSearched.slice(this.page * this.pageSize, (this.page + 1) * this.pageSize);
		},
		businessesSearched() {
			if (this.superStore && this.superStore.businesses) {
				let businesses = this.superStore.businesses;
				if (this.search.length > 0) {
					let lowerSearch = this.search.toLowerCase();
					let startsWithHash = lowerSearch.startsWith('#');
					if (startsWithHash) {
						let withoutHash = lowerSearch.replace('#', '');
						businesses = businesses.filter(b => {
							return b.business_id.toString().startsWith(withoutHash);
						});
					} else {
						businesses = businesses.filter(b => {
							return b.name.toLowerCase().includes(lowerSearch) || (b.link && b.link.toLowerCase().includes(lowerSearch)) || (b.vat && b.vat.includes(lowerSearch));
						});
					}
				}
				return businesses;
			} else {
				return [];
			}
		},
	},
	methods: {
		pageChanged(page, pageSize) {
			this.page = page;
			this.pageSize = pageSize;
		},
		toggleBusiness(business) {
			this.businesses.forEach(b => {
				if (b.business_id === business.business_id) {
					b.active = !b.active;
				} else {
					b.active = false;
				}
			});
		},
		changeSearch(text) {
			this.search = text;
			this.page = 0;
		},
		getBusinessLinkHtml(item) {

			let parts = [];

			// Add visit link
			if (item.link) {
				const href = `https://${item.link}.${this.HOST}`;
				parts.push(`<a href='${href}'>${this.getString('visit_link')}</a>`);
			} else {
				parts.push(this.getString('link_not_set'));
			}
			
			// Add Pipedrive link if applicable
			const pipedriveUrl = this.getPipedriveUrl(item);
			if (pipedriveUrl) {
				parts.push(`<a href='${pipedriveUrl}' target='_blank'>Pipedrive</a>`);
			}

			let html = parts.join(' | ');
			
			return this.$sanitize(html, {allowedTags: ['a'], allowedAttributes: {'a': ['href', 'target']}});
		},
		getPipedriveUrl(item) {
			if (!item.crm_deal_id) {
				return null;
			}
			
			// Check conditions for showing Pipedrive link
			if (!TEST) {
				// Production environment
				return `https://qromo.pipedrive.com/deal/${item.crm_deal_id}`;
			} else if (TEST && ENVIRONMENT === 'qromotest') {
				// Test environment
				return `https://qromotest.pipedrive.com/deal/${item.crm_deal_id}`;
			}
			
			// Don't show link in other cases
			return null;
		},
	},
	mounted() {
		if (this.superStore.businesses === null) {
			this.superStore.fetchBusinesses();
		}
	},
}
</script>

<style lang="scss" scoped>
.super-business-section {
	max-width: 1400px;
	margin: 0 auto;

	:deep(.card-body) {
		padding: 0 !important;
	}

	.container-search {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;

		.title {
			font-size: 18px;
			font-weight: 600;
			color: var(--color-txt);
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			padding-right: 5px;
		}
	}

	.list-item-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		cursor: pointer;

		.list-item-title {
			font-size: 16px;
			font-weight: 600;
			color: var(--color-txt);
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			padding-right: 5px;

			.deleted {
				color: $danger;
				margin-right: 6px;
				font-size: 12px;
				font-weight: 700;
				font-style: italic;
				text-transform: uppercase;
			}

			.verified-badge {
				width: 20px;
				height: 20px;

				:deep(svg) {
					stroke: var(--color-secondary);
				}
			}
		}

		.list-item-business-num {
			font-size: 14px;
			font-weight: 600;
			color: var(--txt-1);
		}
	}

	.pagination-container {
		padding: 0 10px 12px;
	}

	.no-results {
		padding: 10px;
	}

	.business-header-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		gap: 10px;
	}

	.business-link {
		cursor: pointer;
		flex: 1;
		font-size: 14px;
		color: var(--txt-1);
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.active-plan {
		font-size: 12px;
		font-weight: 500;
		color: var(--color-secondary);
		white-space: nowrap;
		flex-shrink: 0;
		text-transform: capitalize;
		opacity: 0.6;
	}
}
</style>
	
